import React, { useState } from 'react';
import { cn } from '@/shared/utils/cn';
import { Icon } from '@/modules/Icon';
import { Badge } from '@/modules/Badge';
import { Button } from '@/modules/Button';
import { SidebarWorkspaceCardProps } from '../../types';
import { SidebarDivider } from '../atoms';
import { faTableLayout, faFolderOpen, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faArrowUpRight } from '@fortawesome/pro-solid-svg-icons';
import { IconType, AnyIconDefinition } from '@/shared/types/types';

/**
 * ============================================
 * SIDEBAR WORKSPACE CARD - MOLECULE
 * ============================================
 * Tarjeta que muestra información del workspace/proyecto actual
 * con acciones rápidas en hover.
 */
export const SidebarWorkspaceCard: React.FC<SidebarWorkspaceCardProps> = ({
  icon = faTableLayout,
  title = 'Mesa de trabajo',
  badge,
  projectId,
  clientName,
  status,
  showActions = false,
  onOpen,
  onClose,
  className,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const showHoverActions = isHovered || showActions;

  // Mapeo de colores de estado
  const statusColorMap = {
    success: 'ui-bg-surface-success-hover ui-text-success',
    warning: 'ui-bg-surface-warning-hover ui-text-warning',
    danger: 'ui-bg-surface-danger ui-text-danger',
    info: 'ui-bg-surface-info-hover ui-text-info',
  };

  const statusIconMap = {
    success: faArrowUpRight,
    warning: faArrowUpRight,
    danger: faArrowUpRight,
    info: faArrowUpRight,
  };

  return (
    <div
      className={cn(
        'ui-flex ui-flex-col ui-gap-8 ui-p-16',
        'ui-border-1 ui-border-default ui-bg-surface-default',
        'ui-w-full ui-rounded-control-sm ui-transition-all',
        className,
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header con icono, título y badge */}
      <div className="ui-flex ui-w-full ui-items-center ui-gap-8">
        {/* Icono con background */}
        <div className="ui-flex ui-shrink-0 ui-items-center ui-justify-center ui-rounded-control-pill ui-bg-surface-brand-hover ui-p-8">
          <Icon name={icon as IconType | AnyIconDefinition} size="md" color="info" />
        </div>

        {/* Título */}
        <span className="ui-flex-1 ui-truncate ui-text-body-sm-regular ui-font-body-sm-regular ui-text-primary">
          {title}
        </span>

        {/* Badge */}
        {badge && (
          <Badge
            label={String(badge.label)}
            color={badge.color || 'accent'}
            theme={badge.theme || 'solid'}
            size="sm"
          />
        )}
      </div>

      {/* Divider */}
      <SidebarDivider />

      {/* Info del proyecto */}
      <div className="ui-flex ui-w-full ui-flex-col ui-items-center ui-text-center">
        {projectId && (
          <span className="ui-w-full ui-truncate ui-text-body-xs-regular ui-font-body-xs-medium ui-text-brand">
            {projectId}
          </span>
        )}
        {clientName && (
          <span className="ui-w-full ui-text-body-xs-regular ui-font-body-xs-regular ui-text-tertiary">
            Cliente: {clientName}
          </span>
        )}
      </div>

      {/* Estado */}
      {status && (
        <div className="ui-w-full">
          <div
            className={cn(
              'ui-flex ui-items-center ui-justify-center ui-gap-4',
              'ui-h-16 ui-w-full ui-rounded-control-xs ui-px-8 ui-py-2',
              statusColorMap[status.color],
            )}
          >
            <Icon name={statusIconMap[status.color]} size="xs" color="current" />
            <span className="ui-text-body-xs-regular ui-font-body-xs-regular">{status.label}</span>
          </div>
        </div>
      )}

      {/* Botones de acción (hover) */}
      {showHoverActions && (
        <div className="ui-flex ui-w-full ui-items-center ui-gap-8">
          <Button
            theme="solid"
            color="tertiary"
            size="xxs"
            fullWidth
            leftSlot={faFolderOpen}
            onClick={onOpen}
          >
            Abrir
          </Button>
          <Button
            theme="solid"
            color="tertiary"
            size="xxs"
            fullWidth
            leftSlot={faXmark}
            onClick={onClose}
          >
            Concluir
          </Button>
        </div>
      )}
    </div>
  );
};
